import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Coins, Loader2, ExternalLink, ArrowUpRight, Infinity, Gift } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '@/components/PageHeader';
import api from '@/lib/api';
import { Link } from 'react-router-dom';

function money(cents) { return `$${(cents/100).toFixed(2)}`; }

export default function BillingPage() {
    const [topups, setTopups] = useState({});
    const [balance, setBalance] = useState(null);
    const [ledger, setLedger] = useState([]);
    const [loading, setLoading] = useState(true);
    const [buying, setBuying] = useState(null);
    const [portalLoading, setPortalLoading] = useState(false);

    const load = async () => {
        try {
            // Note: we intentionally do NOT fetch /billing/transactions here
            // anymore — that endpoint included Stripe checkout sessions that
            // were opened but never completed, surfacing as scary "pending"
            // rows. The authoritative billing history lives in the Stripe
            // customer portal (link below).
            const [pl, bl, lg] = await Promise.all([
                api.get('/billing/plans'),
                api.get('/billing/balance'),
                api.get('/billing/ledger'),
            ]);
            setTopups(pl.data.topups || {});
            setBalance(bl.data);
            setLedger(lg.data.items || []);
        } catch (e) {
            toast.error('Failed to load billing info');
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const buyTopup = async (pack) => {
        setBuying(pack);
        try {
            const r = await api.post('/billing/checkout/topup', { pack_key: pack, origin_url: window.location.origin });
            window.location.href = r.data.url;
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Checkout failed');
            setBuying(null);
        }
    };

    const openPortal = async () => {
        setPortalLoading(true);
        try {
            const r = await api.post('/billing/portal', null, { params: { origin_url: window.location.origin } });
            window.location.href = r.data.url;
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Portal unavailable. Subscribe first.');
            setPortalLoading(false);
        }
    };

    if (loading) return <div className="text-slate-500">Loading…</div>;

    const sub = balance?.subscription || {};
    const credits = balance?.credits || { monthly: 0, topup: 0, total: 0 };
    const isUnlimited = !!credits.unlimited;
    const isActiveSub = !!credits.active;
    const isTrialing = sub?.status === 'trialing' || credits?.subscription_status === 'trialing';
    const isSolo = sub.tier === 'solo';
    // Days remaining in current period — for trials this is days until first charge.
    const daysLeftInPeriod = (() => {
        if (!sub?.current_period_end) return null;
        try {
            const end = new Date(sub.current_period_end);
            const diff = Math.ceil((end - new Date()) / (1000 * 60 * 60 * 24));
            return diff > 0 ? diff : 0;
        } catch { return null; }
    })();

    return (
        <>
            <PageHeader title="Billing" subtitle="Manage your plan and view lead-lookup credits." testId="billing-header" />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                <Card>
                    <CardHeader><CardTitle className="font-display">Current plan</CardTitle></CardHeader>
                    <CardContent>
                        <div className="font-display text-2xl text-slate-900">{sub.tier ? sub.tier[0].toUpperCase() + sub.tier.slice(1) : 'No plan'}</div>
                        <div className="text-sm text-slate-500">{sub.plan_key ? sub.plan_key.replace('_', ' ') : '—'}</div>
                        <div className="mt-3 flex flex-col gap-1 text-sm">
                            <div className="flex justify-between"><span className="text-slate-500">Status</span><Badge variant="outline" className={sub.status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}>{sub.status || 'inactive'}</Badge></div>
                            <div className="flex justify-between"><span className="text-slate-500">Seats</span><span className="font-medium">{(sub.seats_included || 0) + (sub.extra_seats || 0)}</span></div>
                            <div className="flex justify-between"><span className="text-slate-500">Renews</span><span className="font-medium">{sub.current_period_end ? sub.current_period_end.slice(0,10) : '—'}</span></div>
                        </div>
                        {(sub.status === 'active' || sub.status === 'trialing') ? (
                            <Button onClick={openPortal} disabled={portalLoading} className="w-full mt-4 bg-slate-900 hover:bg-slate-800 text-white" data-testid="billing-open-portal-button">
                                {portalLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ExternalLink className="h-4 w-4 mr-1" />} Stripe portal
                            </Button>
                        ) : (
                            <Button asChild className="w-full mt-4 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid="billing-choose-plan-button">
                                <Link to="/pricing">Start 7-day free trial <ArrowUpRight className="h-4 w-4 ml-1" /></Link>
                            </Button>
                        )}
                    </CardContent>
                </Card>

                <Card className="lg:col-span-2">
                    <CardHeader><CardTitle className="font-display">Lead lookups</CardTitle></CardHeader>
                    <CardContent>
                        {isUnlimited ? (
                            <div className="flex items-center gap-3" data-testid="billing-unlimited">
                                <Infinity className="h-12 w-12 text-amber-500" strokeWidth={2} />
                                <div>
                                    <div className="font-display text-2xl text-slate-900">Unlimited</div>
                                    <div className="text-sm text-slate-500 capitalize">Included with your {sub.tier || 'Crew'} plan{isTrialing ? ' (trial)' : ''}</div>
                                </div>
                            </div>
                        ) : isTrialing ? (
                            <div className="flex items-center gap-3" data-testid="billing-trial">
                                <Gift className="h-12 w-12 text-amber-500" strokeWidth={2} />
                                <div>
                                    <div className="font-display text-2xl text-slate-900">7-day free trial active</div>
                                    <div className="text-sm text-slate-500">
                                        <b>{credits.total}</b> lookups available on your {sub.tier || 'Solo'} plan
                                        {daysLeftInPeriod !== null && <> · <b>{daysLeftInPeriod}</b> day{daysLeftInPeriod === 1 ? '' : 's'} until first charge</>}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-wrap gap-6">
                                <div>
                                    <div className="text-xs text-slate-500 uppercase tracking-wider">Monthly</div>
                                    <div className="font-display text-3xl tabular-nums" data-testid="billing-credits-monthly">{credits.monthly}</div>
                                    <div className="text-xs text-slate-500">Resets each billing cycle</div>
                                </div>
                                <div>
                                    <div className="text-xs text-slate-500 uppercase tracking-wider">Top-up</div>
                                    <div className="font-display text-3xl tabular-nums" data-testid="billing-credits-topup">{credits.topup}</div>
                                    <div className="text-xs text-slate-500">Never expire</div>
                                </div>
                                <div>
                                    <div className="text-xs text-slate-500 uppercase tracking-wider">Total available</div>
                                    <div className="font-display text-3xl tabular-nums text-amber-600" data-testid="billing-credits-total">{credits.total}</div>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {isUnlimited ? (
                <Card className="mb-8 border-amber-200 bg-amber-50/40" data-testid="billing-unlimited-banner">
                    <CardContent className="py-5 flex items-center gap-3">
                        <Infinity className="h-6 w-6 text-amber-600" />
                        <div className="text-sm">
                            <b className="text-slate-900 capitalize">{sub.tier || 'Crew'} plan</b> includes unlimited lead lookups and unlimited AI roof estimates — no top-up packs needed.
                        </div>
                    </CardContent>
                </Card>
            ) : isSolo && sub.status === 'active' && Object.keys(topups).length > 0 ? (
                <>
                    <h2 className="font-display text-xl mb-1 text-slate-900">Need more lookups?</h2>
                    <p className="text-sm text-slate-600 mb-3">
                        Top-up packs never expire and are used <i>after</i> your monthly allotment is exhausted. Or
                        <Link to="/pricing" className="text-amber-700 hover:underline ml-1">upgrade to Crew for unlimited</Link>.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                        {Object.entries(topups).map(([key, pack]) => (
                            <Card key={key} className={`border-slate-200 relative ${pack.best_value ? 'ring-2 ring-amber-300' : ''}`} data-testid={`billing-topup-card-${key}`}>
                                {pack.best_value && (
                                    <Badge className="absolute -top-2 left-3 bg-amber-500 text-slate-950 font-semibold border-0">Best value</Badge>
                                )}
                                <CardContent className="pt-5">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <div className="text-xs uppercase tracking-wider text-slate-500">Top-up pack</div>
                                            <div className="font-display text-2xl text-slate-900 tabular-nums">{pack.credits} lookups</div>
                                        </div>
                                        <Coins className="h-5 w-5 text-amber-500" />
                                    </div>
                                    <div className="mt-3 flex items-baseline gap-1">
                                        <span className="font-display text-2xl text-slate-900 tabular-nums">{money(pack.amount)}</span>
                                        <span className="text-slate-500 text-sm">one-time</span>
                                    </div>
                                    <div className="text-xs text-slate-500 mt-1">
                                        ≈ ${(pack.amount / 100 / pack.credits).toFixed(2)} per lookup
                                    </div>
                                    <Button
                                        onClick={() => buyTopup(key)}
                                        disabled={buying === key}
                                        className="w-full mt-4 bg-slate-900 hover:bg-slate-800 text-white font-semibold"
                                        data-testid={`billing-topup-${key}-button`}
                                    >
                                        {buying === key ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : 'Buy now'}
                                    </Button>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </>
            ) : (!isActiveSub) ? (
                <Card className="mb-8">
                    <CardContent className="py-6 text-center">
                        <div className="font-display text-lg text-slate-900 mb-1">Start your 7-day free trial to unlock the app</div>
                        <p className="text-sm text-slate-600 mb-3">Card required upfront — no charge for 7 days. Cancel anytime before day 8. Top-up packs are available after your trial converts to a paid Solo subscription. Crew &amp; Business come with unlimited lookups.</p>
                        <Button asChild className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">
                            <Link to="/pricing">Start free trial <ArrowUpRight className="h-4 w-4 ml-1" /></Link>
                        </Button>
                    </CardContent>
                </Card>
            ) : null}

            <h2 className="font-display text-xl mb-3 text-slate-900">Activity log</h2>
            <Card className="mb-6">
                <CardContent className="p-0">
                    {ledger.length === 0 ? (
                        <div className="p-6 text-slate-500">No activity yet.</div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50"><tr className="text-left"><th className="py-2 px-4 font-medium">When</th><th className="py-2 px-4 font-medium">Type</th><th className="py-2 px-4 font-medium">Change</th><th className="py-2 px-4 font-medium">Reference</th></tr></thead>
                            <tbody>
                                {ledger.map((e, i) => (
                                    <tr key={i} className="border-t">
                                        <td className="py-2 px-4 font-mono text-xs">{e.created_at?.slice(0,16).replace('T',' ')}</td>
                                        <td className="py-2 px-4 capitalize">{(e.kind || '').replace(/_/g, ' ')}</td>
                                        <td className={`py-2 px-4 tabular-nums font-medium ${e.amount < 0 ? 'text-red-600' : (e.amount > 0 ? 'text-emerald-700' : 'text-slate-400')}`}>
                                            {e.amount > 0 ? `+${e.amount}` : (e.amount === 0 ? '—' : e.amount)}
                                        </td>
                                        <td className="py-2 px-4 text-slate-500 truncate max-w-xs">{e.ref}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </CardContent>
            </Card>

            {/* The Transactions table was removed: it surfaced Stripe checkout
                sessions immediately after a user clicked "Subscribe" — even if
                they abandoned the checkout — and the resulting "pending" rows
                made people think they'd been accidentally charged. The Stripe
                customer portal (linked above) shows authoritative billing
                history with proper receipts and statuses. */}
        </>
    );
}
